/**
 * Article Page JavaScript
 * Handles Table of Contents, FAQ Accordion, and Share functionality
 */

document.addEventListener('DOMContentLoaded', function() {
  
  const articleContent = document.querySelector('.article-page__content');
  const tocLinks = document.querySelectorAll('.article-toc__link');
  
  // ============================================
  // Cleanup empty leading tags/gaps in article content
  // ============================================
  if (articleContent) {
    const children = Array.from(articleContent.children);
    for (let child of children) {
      const text = child.textContent.replace(/[\s\uFEFF\xA0]+/g, '').trim();
      if (text === '' && (child.tagName === 'P' || child.tagName === 'DIV' || child.tagName === 'BR')) {
        child.remove();
      } else {
        break;
      }
    }
  }

  // ============================================
  // Auto-assign IDs to h2 headings from TOC
  // ============================================
  
  // Helper function to create a slug from text
  function createSlug(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/^\d+\.\s*/, '')  // Remove leading numbers like "1. " or "2. "
      .replace(/[^\w\s-]/g, '')  // Remove special characters
      .replace(/\s+/g, '-')      // Replace spaces with hyphens
      .replace(/-+/g, '-');      // Replace multiple hyphens with single hyphen
  }
  
  // Helper function to normalize text for comparison (remove numbers, punctuation, lowercase)
  function normalizeText(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/^\d+\.\s*/, '')  // Remove leading numbers
      .replace(/[^\w\s]/g, '')   // Remove punctuation
      .replace(/\s+/g, ' ');     // Normalize spaces
  }
  
  // Helper function to calculate text similarity
  function textSimilarity(text1, text2) {
    const norm1 = normalizeText(text1);
    const norm2 = normalizeText(text2);
    
    // Exact match
    if (norm1 === norm2) return 100;
    
    // Check if one contains the other
    if (norm1.includes(norm2) || norm2.includes(norm1)) return 80;
    
    // Check word overlap
    const words1 = norm1.split(' ');
    const words2 = norm2.split(' ');
    const commonWords = words1.filter(word => words2.includes(word) && word.length > 2);
    const similarity = (commonWords.length / Math.max(words1.length, words2.length)) * 100;
    
    return similarity;
  }
  
  if (tocLinks.length > 0 && articleContent) {
    // Get all h2 headings in the article content
    const h2Headings = articleContent.querySelectorAll('h2');
    
    // For each TOC link, find the best matching h2 heading
    tocLinks.forEach((link) => {
      const textElem = link.querySelector('.article-toc__text');
      const linkText = textElem ? textElem.textContent.trim() : link.textContent.trim();
      let bestMatch = null;
      let bestScore = 0;
      
      // Find the h2 heading with the highest similarity score
      h2Headings.forEach(heading => {
        if (heading.id) return; // Skip if already assigned
        
        const headingText = heading.textContent.trim();
        const score = textSimilarity(linkText, headingText);
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = heading;
        }
      });
      
      // If we found a good match (score > 50), assign the ID
      if (bestMatch && bestScore > 50) {
        const slugId = createSlug(bestMatch.textContent);
        bestMatch.id = slugId;
        link.setAttribute('href', '#' + slugId);
        
        console.log(`Matched TOC "${linkText}" to heading "${bestMatch.textContent}" (score: ${bestScore})`);
      } else {
        console.warn(`No good match found for TOC item "${linkText}"`);
      }
    });
  }
  
  // ============================================
  // Smooth Scroll for Table of Contents
  // ============================================
  
  tocLinks.forEach(link => {
    link.addEventListener('click', function(e) {
      e.preventDefault();
      
      // Get the ID from the href
      const targetId = this.getAttribute('href').replace('#', '');
      
      // Find the element by ID (should work now after auto-assignment)
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        const offset = 100; // Offset for fixed header
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        // Add ripple effect in red
        this.style.position = 'relative';
        const ripple = document.createElement('span');
        ripple.style.cssText = `
          position: absolute;
          border-radius: 50%;
          background: rgba(181, 51, 36, 0.25);
          width: 10px;
          height: 10px;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) scale(0);
          animation: ripple 0.6s ease-out;
          pointer-events: none;
        `;
        this.appendChild(ripple);
        setTimeout(() => ripple.remove(), 600);
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Update active state with animation
        tocLinks.forEach(l => {
          l.classList.remove('active');
        });
        this.classList.add('active');
        
        // Highlight the target element briefly
        targetElement.style.transition = 'background-color 0.6s ease';
        targetElement.style.backgroundColor = 'rgba(181, 51, 36, 0.08)';
        setTimeout(() => {
          targetElement.style.backgroundColor = '';
        }, 1500);
      } else {
        console.warn('Target element not found for ID:', targetId);
      }
    });
  });

  // Add ripple animation to CSS dynamically
  if (!document.getElementById('toc-ripple-style')) {
    const style = document.createElement('style');
    style.id = 'toc-ripple-style';
    style.textContent = `
      @keyframes ripple {
        to {
          transform: translate(-50%, -50%) scale(4);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ============================================
  // FAQ Accordion Functionality with Animations
  // ============================================
  const faqQuestions = document.querySelectorAll('.article-faq__question');
  
  faqQuestions.forEach(question => {
    question.addEventListener('click', function() {
      const isExpanded = this.getAttribute('aria-expanded') === 'true';
      const answer = this.nextElementSibling;
      const faqItem = this.parentElement;
      
      // Close all other FAQs (optional - remove if you want multiple open)
      faqQuestions.forEach(otherQuestion => {
        if (otherQuestion !== question) {
          const otherAnswer = otherQuestion.nextElementSibling;
          const otherItem = otherQuestion.parentElement;
          otherQuestion.setAttribute('aria-expanded', 'false');
          otherAnswer.hidden = true;
          otherItem.classList.remove('is-open');
        }
      });
      
      // Toggle current FAQ
      this.setAttribute('aria-expanded', !isExpanded);
      
      if (isExpanded) {
        // Closing
        answer.hidden = true;
        faqItem.classList.remove('is-open');
      } else {
        // Opening
        answer.hidden = false;
        faqItem.classList.add('is-open');
      }
    });
  });

  // ============================================
  // Share Button Functionality
  // ============================================
  const shareButton = document.querySelector('.article-share__button');
  
  if (shareButton) {
    shareButton.addEventListener('click', function() {
      if (navigator.share) {
        // Use native share API (mobile)
        navigator.share({
          title: document.title,
          url: window.location.href
        }).catch(err => console.log('Error sharing:', err));
      } else {
        // Fallback: copy to clipboard (desktop)
        navigator.clipboard.writeText(window.location.href).then(() => {
          // Show success message
          const originalText = this.querySelector('span').textContent;
          this.querySelector('span').textContent = 'Link copied!';
          
          // Reset after 2 seconds
          setTimeout(() => {
            this.querySelector('span').textContent = originalText;
          }, 2000);
        }).catch(err => {
          console.error('Failed to copy:', err);
          alert('Failed to copy link. Please copy manually: ' + window.location.href);
        });
      }
    });
  }

  // ============================================
  // Highlight Active TOC Item on Scroll
  // ============================================
  if (tocLinks.length > 0) {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -66%',
      threshold: 0
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('id') || entry.target.getAttribute('data-id');
          if (id) {
            tocLinks.forEach(link => {
              link.classList.remove('active');
              let linkHref = link.getAttribute('href');
              if (linkHref.startsWith('#')) {
                linkHref = linkHref.substring(1);
              }
              if (linkHref === id) {
                link.classList.add('active');
              }
            });
          }
        }
      });
    }, observerOptions);

    // Observe all h2 headings that now have IDs assigned
    tocLinks.forEach(link => {
      const targetId = link.getAttribute('href').replace('#', '');
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        observer.observe(targetElement);
      }
    });
  }

  // ============================================
  // Smooth Scroll Polyfill for Older Browsers
  // ============================================
  if (!('scrollBehavior' in document.documentElement.style)) {
    const smoothScrollPolyfill = function(target, duration) {
      const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - 80;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      function animation(currentTime) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = ease(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }

        function ease(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      }

      requestAnimationFrame(animation);
    };
  }

});
